import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import adminAPI from '../../services/adminAPI';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';
import useDialogKeys from '../../hooks/useDialogKeys';
import { ROLES, MIN_PASSWORD_LENGTH, displayName, initials, generatePassword, lastSignIn } from './userFormat';

const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const sameIds = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

function PasswordField({ id, label, value, onChange, required, hint }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    onChange(generatePassword());
    setVisible(true);
    setCopied(false);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="um-field">
      <label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      <div className="um-password">
        <input id={id} type={visible ? 'text' : 'password'} autoComplete="new-password" value={value} required={required} minLength={MIN_PASSWORD_LENGTH} onChange={(e) => { onChange(e.target.value); setCopied(false); }} />
        <button type="button" className="um-link-button" onClick={() => setVisible((v) => !v)} aria-label={visible ? 'Hide password' : 'Show password'}>{visible ? 'Hide' : 'Show'}</button>
      </div>
      <div className="um-password-actions">
        <button type="button" className="um-link-button" onClick={generate}>Generate a password</button>
        {value && visible && <button type="button" className="um-link-button" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>}
      </div>
      <small>{hint}</small>
    </div>
  );
}

/**
 * Add or edit one user in a side panel. Nothing changes until Save, and closing
 * with unsaved changes asks first. Companies and dashboards are shown only when
 * the chosen role uses them.
 */
const UserDrawer = ({ user = null, companies = [], dashboards = [], currentUser, onClose, onSaved }) => {
  const creating = !user;
  const isSelf = !creating && user.id === currentUser?.id;
  const isSuperAdmin = currentUser?.role === 'super_admin';
  // Deactivating and deleting follow the same rule as the API.
  const canManageAccount = !creating && !isSelf && (isSuperAdmin || user.role === 'user');

  const initial = useMemo(() => {
    // An admin sees and changes only their own companies; the API keeps the rest.
    const visible = new Set(companies.map((c) => c.id));
    const assigned = (user?.companies || []).map((c) => c.id).filter((id) => visible.has(id));
    return {
      username: user?.username || '',
      fullName: user?.full_name || '',
      email: user?.email || '',
      role: user?.role || 'user',
      // A company admin usually has one company: choose it for them when adding a user.
      companyIds: creating && companies.length === 1 ? [companies[0].id] : assigned,
    };
  }, [user, creating, companies]);

  const [form, setForm] = useState({ ...initial, password: '' });
  const [dashboardIds, setDashboardIds] = useState([]);
  const [savedDashboardIds, setSavedDashboardIds] = useState([]);
  const [loadingDashboards, setLoadingDashboards] = useState(!creating && user?.role === 'user');
  const [resetPassword, setResetPassword] = useState(creating);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'discard' | 'deactivate' | 'delete'

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  useEffect(() => {
    if (creating || user.role !== 'user') return undefined;
    let cancelled = false;
    adminAPI.users.getDashboards(user.id)
      .then((res) => {
        if (cancelled || !res?.success) return;
        const ids = res.data.map((d) => d.id);
        setDashboardIds(ids);
        setSavedDashboardIds(ids);
      })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Could not load this user\'s dashboards')); })
      .finally(() => { if (!cancelled) setLoadingDashboards(false); });
    return () => { cancelled = true; };
  }, [creating, user]);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // The list of companies can arrive after the panel opens.
  const companiesTouched = useRef(false);
  useEffect(() => {
    if (!companiesTouched.current) setForm((f) => ({ ...f, companyIds: initial.companyIds }));
  }, [initial.companyIds]);

  const usesCompanies = form.role !== 'super_admin';
  const usesDashboards = form.role === 'user';
  const availableDashboards = dashboards.filter((d) => form.companyIds.includes(d.company_id));
  const chosenDashboards = dashboardIds.filter((id) => availableDashboards.some((d) => d.id === id));

  const dirty = form.username !== initial.username || form.fullName !== initial.fullName || form.email !== initial.email
    || form.role !== initial.role || form.password !== '' || !sameIds(form.companyIds, initial.companyIds)
    || !sameIds(chosenDashboards, savedDashboardIds.filter((id) => dashboards.some((d) => d.id === id)));

  const requestClose = () => {
    if (busy) return;
    if (confirm) setConfirm(null);
    else if (dirty) setConfirm('discard');
    else onClose();
  };
  const overlay = useModalOverlayClose(requestClose);
  useDialogKeys(dialogRef, requestClose);

  const toggle = (setter, id) => setter((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleCompany = (id) => { companiesTouched.current = true; setForm((f) => ({ ...f, companyIds: f.companyIds.includes(id) ? f.companyIds.filter((x) => x !== id) : [...f.companyIds, id] })); };

  const validate = () => {
    if (usesCompanies && form.companyIds.length === 0) return 'Choose at least one company.';
    if (form.password && form.password.length < MIN_PASSWORD_LENGTH) return `The password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    return '';
  };

  const save = async (e) => {
    e.preventDefault();
    const problem = validate();
    setError(problem);
    if (problem) return;
    setBusy(true);
    let created = null;
    try {
      if (creating) {
        const res = await adminAPI.users.create({
          username: form.username.trim(), email: form.email.trim(), password: form.password, fullName: form.fullName.trim(), role: form.role,
          companyIds: usesCompanies ? form.companyIds : [],
        });
        if (!res?.success) throw new Error(res?.error || 'Could not add the user');
        created = res.data;
      } else {
        const res = await adminAPI.users.update(user.id, {
          email: form.email.trim(), fullName: form.fullName.trim(), role: form.role, ...(form.password ? { password: form.password } : {}),
        });
        if (!res?.success) throw new Error(res?.error || 'Could not save the user');
        if (usesCompanies) await adminAPI.users.updateCompanies(user.id, form.companyIds);
      }
      const id = creating ? created.id : user.id;
      if (usesDashboards && (!creating || chosenDashboards.length > 0)) await adminAPI.users.updateDashboards(id, chosenDashboards);
      onSaved(creating ? `Added ${form.fullName.trim() || form.username.trim()}` : `Saved ${form.fullName.trim() || user.username}`);
    } catch (err) {
      setBusy(false);
      // The account exists by now: say so, as trying to add it again would fail.
      if (created) onSaved(`Added ${created.username}, but their dashboards could not be saved. Open the user to choose them again.`, 'error');
      else setError(errorText(err, 'Could not save the user'));
    }
  };

  const setActive = async (isActive) => {
    setBusy(true);
    setError('');
    try {
      await adminAPI.users.setActive(user.id, isActive);
      onSaved(`${isActive ? 'Reactivated' : 'Deactivated'} ${displayName(user)}`);
    } catch (err) {
      setBusy(false);
      setConfirm(null);
      setError(errorText(err, 'Could not change the status'));
    }
  };

  const resetTwoFactor = async () => {
    setBusy(true);
    setError('');
    try {
      await adminAPI.users.resetTwoFactor(user.id);
      onSaved(`Reset two-step verification for ${displayName(user)}. They will set it up again when they next sign in.`);
    } catch (err) {
      setBusy(false);
      setConfirm(null);
      setError(errorText(err, 'Could not reset two-step verification'));
    }
  };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await adminAPI.users.delete(user.id);
      onSaved(`Deleted ${displayName(user)}`);
    } catch (err) {
      setBusy(false);
      setConfirm(null);
      setError(errorText(err, 'Could not delete the user'));
    }
  };

  const companyNames = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);
  const dashboardGroups = form.companyIds
    .map((id) => ({ id, name: companyNames.get(id), items: availableDashboards.filter((d) => d.company_id === id) }))
    .filter((g) => g.items.length > 0);
  const roles = ROLES.filter((r) => r.value !== 'super_admin' || isSuperAdmin);
  const title = creating ? 'Add user' : `Edit ${displayName(user)}`;

  // Rendered on the page body so it covers the whole app.
  return createPortal(
    <div className="modal-overlay" {...overlay}>
      <form className="um-drawer" role="dialog" aria-modal="true" aria-label={title} ref={dialogRef} onSubmit={save} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <header className="um-drawer-header">
          {creating ? <h2>Add user</h2> : (
            <div className="um-identity">
              <span className="um-avatar um-avatar--large" aria-hidden="true">{initials(user)}</span>
              <div>
                <h2>{displayName(user)}{isSelf && <span className="um-you">You</span>}</h2>
                <p>{user.username} · Last sign-in: {lastSignIn(user.last_login_at).toLowerCase()}</p>
              </div>
            </div>
          )}
          <button type="button" className="editor-drawer-close" onClick={requestClose} aria-label="Close">×</button>
        </header>

        <div className="um-drawer-body">
          {error && <div className="um-error" role="alert">{error}</div>}

          <section className="um-section">
            <h3>Details</h3>
            <div className="um-field">
              <label htmlFor="um-full-name">Full name</label>
              <input id="um-full-name" ref={firstFieldRef} type="text" autoComplete="off" value={form.fullName} onChange={set('fullName')} />
            </div>
            <div className="um-field">
              <label htmlFor="um-email">Email<span aria-hidden="true"> *</span></label>
              <input id="um-email" type="email" autoComplete="off" required value={form.email} onChange={set('email')} />
            </div>
            {creating && (
              <div className="um-field">
                <label htmlFor="um-username">Username<span aria-hidden="true"> *</span></label>
                <input id="um-username" type="text" autoComplete="off" required minLength={3} value={form.username} onChange={set('username')} />
                <small>Used to sign in. It cannot be changed later.</small>
              </div>
            )}
          </section>

          <section className="um-section">
            <h3 id="um-role-heading">Role</h3>
            {isSelf && <p className="um-note">You cannot change your own role.</p>}
            <div className="um-roles" role="radiogroup" aria-labelledby="um-role-heading">
              {roles.map((r) => (
                <label key={r.value} className={`um-role ${form.role === r.value ? 'selected' : ''} ${isSelf ? 'disabled' : ''}`}>
                  <input type="radio" name="um-role" value={r.value} checked={form.role === r.value} disabled={isSelf} onChange={set('role')} />
                  <span><strong>{r.label}</strong><small>{r.description}</small></span>
                </label>
              ))}
            </div>
          </section>

          {usesCompanies && (
            <section className="um-section">
              <h3>Companies<span aria-hidden="true"> *</span></h3>
              {companies.length === 0 ? <p className="um-note">Loading companies…</p> : (
                <div className="um-checks">
                  {companies.map((c) => (
                    <label key={c.id} className="um-check">
                      <input type="checkbox" checked={form.companyIds.includes(c.id)} onChange={() => toggleCompany(c.id)} />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </section>
          )}

          {usesDashboards && (
            <section className="um-section">
              <div className="um-section-title">
                <h3>Dashboards</h3>
                {availableDashboards.length > 1 && (
                  <button type="button" className="um-link-button" onClick={() => setDashboardIds(chosenDashboards.length === availableDashboards.length ? [] : availableDashboards.map((d) => d.id))}>
                    {chosenDashboards.length === availableDashboards.length ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
              {loadingDashboards ? <p className="um-note">Loading dashboards…</p>
                : form.companyIds.length === 0 ? <p className="um-note">Choose a company to see its dashboards.</p>
                  : dashboardGroups.length === 0 ? <p className="um-note">These companies have no dashboards yet.</p>
                    : dashboardGroups.map((g) => (
                      <div key={g.id} className="um-dashboard-group">
                        {dashboardGroups.length > 1 && <h4>{g.name}</h4>}
                        <div className="um-checks">
                          {g.items.map((d) => (
                            <label key={d.id} className="um-check">
                              <input type="checkbox" checked={dashboardIds.includes(d.id)} onChange={() => toggle(setDashboardIds, d.id)} />
                              <span>{d.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
              {!loadingDashboards && availableDashboards.length > 0 && chosenDashboards.length === 0 && (
                <p className="um-warning">With no dashboards chosen, this user will see nothing after signing in.</p>
              )}
            </section>
          )}

          <section className="um-section">
            <h3>Password</h3>
            {resetPassword ? (
              <PasswordField id="um-password" label={creating ? 'Password' : 'New password'} required={creating} value={form.password}
                onChange={(password) => setForm((f) => ({ ...f, password }))}
                hint={`At least ${MIN_PASSWORD_LENGTH} characters. Share it with them securely; they can change it from their profile.`} />
            ) : (
              <button type="button" className="btn-secondary um-inline-button" onClick={() => setResetPassword(true)}>Set a new password</button>
            )}
          </section>

          {canManageAccount && (
            <section className="um-section um-account">
              <h3>Account</h3>
              <div className="um-account-row">
                <div>
                  <strong>{user.is_active ? 'Deactivate' : 'Reactivate'}</strong>
                  <small>{user.is_active ? 'They can no longer sign in. Everything is kept and you can reactivate them at any time.' : 'They can sign in again with their existing password.'}</small>
                </div>
                {user.is_active
                  ? <button type="button" className="btn-secondary um-inline-button" disabled={busy} onClick={() => setConfirm('deactivate')}>Deactivate</button>
                  : <button type="button" className="btn-secondary um-inline-button" disabled={busy} onClick={() => setActive(true)}>Reactivate</button>}
              </div>
              <div className="um-account-row">
                <div>
                  <strong>Two-step verification: {user.two_factor ? 'on' : 'not set up yet'}</strong>
                  <small>{user.two_factor ? 'If they lose their phone, reset it: they are signed out and set it up again when they next sign in.' : 'They will be asked to set it up when they next sign in.'}</small>
                </div>
                {user.two_factor && <button type="button" className="btn-secondary um-inline-button" disabled={busy} onClick={() => setConfirm('reset2fa')}>Reset</button>}
              </div>
              <div className="um-account-row">
                <div>
                  <strong>Delete</strong>
                  <small>Permanently removes this user. Reports they created are kept.</small>
                </div>
                <button type="button" className="um-danger-button" disabled={busy} onClick={() => setConfirm('delete')}>Delete user</button>
              </div>
            </section>
          )}
        </div>

        {confirm ? (
          <footer className="um-drawer-footer um-confirm" role="alertdialog" aria-label="Confirm">
            <p>
              {confirm === 'discard' && 'Discard your unsaved changes?'}
              {confirm === 'deactivate' && `Deactivate ${displayName(user)}? They will be signed out straight away.`}
              {confirm === 'delete' && `Delete ${displayName(user)}? This cannot be undone.`}
              {confirm === 'reset2fa' && `Reset two-step verification for ${displayName(user)}? They will be signed out and set it up again with their phone.`}
            </p>
            <div className="um-footer-actions">
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirm(null)}>{confirm === 'discard' ? 'Keep editing' : 'Cancel'}</button>
              {confirm === 'discard' && <button type="button" className="um-danger-solid" onClick={onClose}>Discard</button>}
              {confirm === 'deactivate' && <button type="button" className="um-danger-solid" disabled={busy} onClick={() => setActive(false)}>Deactivate</button>}
              {confirm === 'delete' && <button type="button" className="um-danger-solid" disabled={busy} onClick={remove}>Delete user</button>}
              {confirm === 'reset2fa' && <button type="button" className="um-danger-solid" disabled={busy} onClick={resetTwoFactor}>Reset</button>}
            </div>
          </footer>
        ) : (
          <footer className="um-drawer-footer">
            <div className="um-footer-actions">
              <button type="button" className="btn-secondary" disabled={busy} onClick={requestClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || loadingDashboards || (!creating && !dirty)}>
                {busy ? 'Saving…' : creating ? 'Add user' : 'Save changes'}
              </button>
            </div>
          </footer>
        )}
      </form>
    </div>,
    document.body
  );
};

export default UserDrawer;
